/**
 * Locking a piece: T-spin detection, writing the cells, and the hard drop.
 *
 * The order matters and is not arbitrary. The three-corner test has to run
 * *before* the piece is written to the board, because the piece's own cells
 * would otherwise fill its own corners.
 */
import { cellAt, setCell } from './board.js';
import { cancelPendingGarbage } from './garbage.js';
import { pieceCellsAt } from './pieces.js';
import { activePiece, collides, dropDistance, spawnPiece, tryMove } from './piece.js';
import { resolveClears } from './resolve.js';
import type { ActivePiece, GameState, LockResult } from './types.js';

/** The four diagonal neighbours of a T's centre. */
const CORNERS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
];

/**
 * How many of the four corners around a T's centre are occupied.
 *
 * The walls and the floor count as occupied; open space above the buffer does
 * not, which matches the reference prototype — a T cannot be spun against the
 * sky.
 */
export function filledCorners(state: GameState, piece: ActivePiece): number {
  const board = state.board;
  const cx = piece.x + 1;
  const cy = piece.y + 1;
  let filled = 0;

  for (const [dx, dy] of CORNERS) {
    const x = cx + dx;
    const y = cy + dy;
    if (x < 0 || x >= board.width || y >= board.height) {
      filled += 1;
      continue;
    }
    if (y >= 0 && cellAt(board, x, y) !== null) filled += 1;
  }

  return filled;
}

/**
 * The three-corner rule from `docs/DESIGN.md`.
 *
 * The piece is a T, the last successful action that changed it was a rotation,
 * and at least three of the four corners around its centre are occupied or out
 * of bounds.
 */
export function isTSpin(state: GameState, slot = 0): boolean {
  const piece = activePiece(state, slot);
  if (piece === null || piece.kind !== 'T') return false;
  if (!piece.lastActionWasRotation) return false;
  return filledCorners(state, piece) >= 3;
}

/**
 * Lift every *other* slot's piece clear of a stack that moved under it.
 *
 * A clear pulls everything above the cleared row down one, and a partner's
 * piece hovering under an overhang can find that stack arriving where it is.
 * The rule mirrors the lift `applyGarbageLines` gives the pieces when the
 * stack rises: move it straight up until it is clear, and if
 * there is nowhere left above the buffer to be, the well has filled up and the
 * game tops out.
 *
 * A one-slot well never enters the loop.
 */
function liftOtherPieces(state: GameState, slot: number): void {
  if (state.pieces.length < 2) return;
  for (const [index, piece] of state.pieces.entries()) {
    if (index === slot || piece === null) continue;
    let guard = state.board.height + 1;
    while (guard > 0 && collides(state, piece.kind, piece.rotation, piece.x, piece.y, index)) {
      piece.y -= 1;
      guard -= 1;
    }
    if (collides(state, piece.kind, piece.rotation, piece.x, piece.y, index)) state.topOut = true;
  }
}

/**
 * Write the piece into the board, resolve the clear, and spawn the next piece.
 *
 * Returns null only when the slot had no piece. A piece that locks entirely
 * inside the hidden rows is a lock-out and tops the game out.
 */
export function lockPiece(state: GameState, slot = 0): LockResult | null {
  const piece = activePiece(state, slot);
  if (piece === null) return null;

  const tSpin = isTSpin(state, slot);
  const cells = pieceCellsAt(piece.kind, piece.rotation, piece.x, piece.y);

  let lockedOut = true;
  for (const [x, y] of cells) {
    setCell(state.board, x, y, piece.kind);
    if (y >= state.board.hiddenRows) lockedOut = false;
  }
  if (lockedOut) state.topOut = true;

  state.piecesPlaced += 1;

  const outcome = resolveClears(state, tSpin);
  liftOtherPieces(state, slot);
  const garbageCancelled = cancelPendingGarbage(state, outcome.attack);

  spawnPiece(state, slot, null);

  return {
    slot,
    kind: piece.kind,
    lines: outcome.lines,
    clearedRows: outcome.clearedRows,
    chain: outcome.chain,
    tSpin: outcome.tSpin,
    perfectClear: outcome.perfectClear,
    attack: outcome.attack,
    garbageCancelled,
    attackSent: outcome.attack - garbageCancelled,
    combo: outcome.combo,
    backToBack: outcome.backToBack,
    lockedOut,
  };
}

/**
 * Drop the piece as far as it goes and lock it immediately.
 *
 * The zero-distance case does not move the piece at all, on purpose. Moving it
 * by zero would still count as a displacement and clear the rotation flag, and
 * a T-spin is almost always finished by rotating into the slot — where the
 * piece is already resting — and then hard dropping.
 */
export function hardDrop(state: GameState, slot = 0): LockResult | null {
  const piece = activePiece(state, slot);
  if (piece === null) return null;
  const distance = dropDistance(state, slot);
  if (distance > 0) tryMove(state, slot, 0, distance);
  return lockPiece(state, slot);
}
